'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface DebtItem {
  id: string;
  name: string;
  effortDays: number;
  impactScore: number; // 1-10
  priority: 'critical' | 'high' | 'medium' | 'low';
}

export default function TechDebtPaydownSimulatorPage() {
  // Current State
  const [currentDebtDays, setCurrentDebtDays] = useState<number>(120);
  const [teamSize, setTeamSize] = useState<number>(8);
  const [sprintLengthWeeks, setSprintLengthWeeks] = useState<number>(2);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'tech-debt-paydown-simulator',
    toolCategory: 'analyzer',
  });

  // Time Allocation
  const [debtAllocationPercent, setDebtAllocationPercent] = useState<number>(20);

  // Velocity Impact
  const [currentVelocity, setCurrentVelocity] = useState<number>(40);
  const [velocityImpactPercent, setVelocityImpactPercent] = useState<number>(25);

  // Debt Items
  const [debtItems, setDebtItems] = useState<DebtItem[]>([
    { id: '1', name: 'Monolith refactoring', effortDays: 30, impactScore: 9, priority: 'critical' },
    { id: '2', name: 'Test coverage improvement', effortDays: 20, impactScore: 7, priority: 'high' },
    { id: '3', name: 'Legacy API deprecation', effortDays: 15, impactScore: 6, priority: 'medium' },
  ]);

  // Simulation Parameters
  const [simulationMonths, setSimulationMonths] = useState<number>(12);

  // Calculations
  const calculations = useMemo(() => {
    const workingDaysPerMonth = 20;
    const sprintsPerMonth = 4 / sprintLengthWeeks;
    const teamWorkingDays = teamSize * workingDaysPerMonth;

    // Days allocated to debt per month
    const debtDaysPerMonth = teamWorkingDays * (debtAllocationPercent / 100);

    // Velocity improvement as debt is paid down
    const maxVelocityGain = (velocityImpactPercent / 100) * currentVelocity;

    // Simulate paydown over time
    const months = [];
    let remainingDebt = currentDebtDays;
    let cumulativeVelocityGain = 0;

    for (let month = 0; month <= simulationMonths; month++) {
      const debtProgress = Math.max(0, 1 - (remainingDebt / currentDebtDays));
      const velocityGain = debtProgress * maxVelocityGain;
      const adjustedVelocity = currentVelocity + velocityGain;

      months.push({
        month,
        remainingDebt: Math.max(0, remainingDebt),
        debtProgress: debtProgress * 100,
        velocity: adjustedVelocity,
        velocityGain,
        debtPaidThisMonth: month > 0 ? Math.min(debtDaysPerMonth, remainingDebt) : 0,
      });

      remainingDebt = Math.max(0, remainingDebt - debtDaysPerMonth);
      cumulativeVelocityGain = velocityGain;
    }

    // Calculate break-even point
    const monthsToPaydown = Math.ceil(currentDebtDays / debtDaysPerMonth);
    const totalSprintsDuringPaydown = monthsToPaydown * sprintsPerMonth;

    // ROI calculation
    const opportunityCostPoints = totalSprintsDuringPaydown * currentVelocity * (debtAllocationPercent / 100);
    const velocityGainPerSprint = maxVelocityGain / sprintsPerMonth;
    const sprintsToBreakEven = Math.ceil(opportunityCostPoints / velocityGainPerSprint);
    const monthsToBreakEven = sprintsToBreakEven / sprintsPerMonth;

    // Total ROI over simulation period
    const totalOpportunityCost = opportunityCostPoints;
    const sprintsAfterPaydown = Math.max(0, (simulationMonths - monthsToPaydown) * sprintsPerMonth);
    const totalVelocityGain = sprintsAfterPaydown * velocityGainPerSprint;
    const netROI = totalVelocityGain - totalOpportunityCost;

    // Prioritized debt items
    const sortedDebtItems = [...debtItems]
      .filter(d => d.name.trim())
      .sort((a, b) => {
        const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        if (priorityOrder[b.priority] !== priorityOrder[a.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return b.impactScore - a.impactScore;
      });

    return {
      months,
      debtDaysPerMonth,
      monthsToPaydown,
      monthsToBreakEven,
      maxVelocityGain,
      totalOpportunityCost,
      totalVelocityGain,
      netROI,
      sortedDebtItems,
      isPositiveROI: netROI > 0,
    };
  }, [currentDebtDays, teamSize, sprintLengthWeeks, debtAllocationPercent, currentVelocity, velocityImpactPercent, simulationMonths, debtItems]);

  // Debt item functions
  const addDebtItem = () => {
    setDebtItems([
      ...debtItems,
      { id: Date.now().toString(), name: '', effortDays: 10, impactScore: 5, priority: 'medium' },
    ]);
  };

  const updateDebtItem = (id: string, field: keyof DebtItem, value: string | number) => {
    setDebtItems(debtItems.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  const removeDebtItem = (id: string) => {
    setDebtItems(debtItems.filter(d => d.id !== id));
  };

  // Export to Excel
  const exportToExcel = () => {
    if (typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    script.onload = () => {
      const XLSX = (window as any).XLSX;
      const wb = XLSX.utils.book_new();

      // Sheet 1: Summary
      const summaryData = [
        ['Technical Debt Paydown Simulation', ''],
        ['', ''],
        ['Current State', ''],
        ['Current Debt (days)', currentDebtDays],
        ['Team Size', teamSize],
        ['Sprint Length (weeks)', sprintLengthWeeks],
        ['Current Velocity (points)', currentVelocity],
        ['', ''],
        ['Allocation', ''],
        ['Debt Time Allocation', debtAllocationPercent + '%'],
        ['Debt Days per Month', calculations.debtDaysPerMonth.toFixed(1)],
        ['', ''],
        ['Projections', ''],
        ['Months to Paydown', calculations.monthsToPaydown],
        ['Months to Break-Even', calculations.monthsToBreakEven.toFixed(1)],
        ['Max Velocity Gain', calculations.maxVelocityGain.toFixed(1) + ' points'],
        ['', ''],
        ['ROI Analysis', ''],
        ['Opportunity Cost', calculations.totalOpportunityCost.toFixed(0) + ' points'],
        ['Total Velocity Gain', calculations.totalVelocityGain.toFixed(0) + ' points'],
        ['Net ROI', calculations.netROI.toFixed(0) + ' points'],
        ['ROI Status', calculations.isPositiveROI ? 'Positive' : 'Negative'],
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      ws1['!cols'] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

      // Sheet 2: Monthly Projection
      const projectionData = calculations.months.map(m => ({
        'Month': m.month,
        'Remaining Debt (days)': m.remainingDebt.toFixed(1),
        'Debt Progress (%)': m.debtProgress.toFixed(1) + '%',
        'Velocity (points)': m.velocity.toFixed(1),
        'Velocity Gain': m.velocityGain.toFixed(1),
        'Debt Paid This Month': m.debtPaidThisMonth.toFixed(1),
      }));

      const ws2 = XLSX.utils.json_to_sheet(projectionData);
      ws2['!cols'] = [{ wch: 8 }, { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 15 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Monthly Projection');

      // Sheet 3: Debt Items
      const debtData = calculations.sortedDebtItems.map(d => ({
        'Priority': d.priority.toUpperCase(),
        'Debt Item': d.name,
        'Effort (days)': d.effortDays,
        'Impact (1-10)': d.impactScore,
      }));

      const ws3 = XLSX.utils.json_to_sheet(debtData);
      ws3['!cols'] = [{ wch: 12 }, { wch: 30 }, { wch: 15 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'Debt Items');

      (wb.Workbook as any) = { CalcPr: { fullCalcOnLoad: true } };
      XLSX.writeFile(wb, 'tech-debt-paydown-simulation.xlsx');
    };
    document.head.appendChild(script);
  };

  const getROIColor = () => {
    if (calculations.isPositiveROI) return 'text-green-500';
    return 'text-red-500';
  };

  const getROIBgColor = () => {
    if (calculations.isPositiveROI) return 'bg-green-500/10 border-green-500/30';
    return 'bg-red-500/10 border-red-500/30';
  };

  const getPriorityColor = (priority: DebtItem['priority']) => {
    switch (priority) {
      case 'critical': return 'bg-red-500/20 text-red-500';
      case 'high': return 'bg-accent-muted text-accent';
      case 'medium': return 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-500';
      case 'low': return 'bg-gray-200 dark:bg-background text-light-text dark:text-foreground';
    }
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const gaps = calculations.sortedDebtItems
      .filter(item => item.priority === 'critical' || item.priority === 'high')
      .map(item => ({
        id: item.id,
        title: item.name,
        severity: item.priority === 'critical' ? 'critical' as const : 'high' as const,
        description: `Effort: ${item.effortDays} days, Impact: ${item.impactScore}/10`,
      }));

    const recs = [
      {
        id: 'paydown-strategy',
        title: `Allocate ${debtAllocationPercent}% to debt paydown`,
        priority: 'high' as const,
        description: `${calculations.debtDaysPerMonth.toFixed(1)} days/month, paydown in ${calculations.monthsToPaydown} months`,
      },
      {
        id: 'velocity-improvement',
        title: `Target velocity improvement of ${calculations.maxVelocityGain.toFixed(1)} points`,
        priority: 'medium' as const,
        description: `Break-even in ${calculations.monthsToBreakEven.toFixed(1)} months`,
      },
      ...calculations.sortedDebtItems.slice(0, 3).map(item => ({
        id: item.id,
        title: `Address: ${item.name}`,
        priority: item.priority === 'critical' ? 'high' as const : 'medium' as const,
        description: `${item.effortDays} days effort, ${item.impactScore}/10 impact`,
      })),
    ];

    const scores: Record<string, number> = {
      'roi': calculations.isPositiveROI ? Math.min(100, 50 + calculations.netROI / 10) : Math.max(0, 50 + calculations.netROI / 10),
      'velocity-potential': (calculations.maxVelocityGain / currentVelocity) * 100,
      'debt-coverage': Math.min(100, (debtAllocationPercent / 25) * 100),
    };

    const overallScore = calculations.isPositiveROI ?
      Math.min(100, 70 + (calculations.netROI / calculations.totalOpportunityCost) * 30) :
      Math.max(0, 50 - (Math.abs(calculations.netROI) / calculations.totalOpportunityCost) * 50);

    const assessment = await saveResult({
      name: `Debt Paydown Simulation - ${new Date().toLocaleDateString()}`,
      score_overall: overallScore,
      scores,
      answers: {
        currentDebtDays,
        teamSize,
        sprintLengthWeeks,
        debtAllocationPercent,
        currentVelocity,
        velocityImpactPercent,
        simulationMonths,
      },
      gaps,
      recommendations: recs,
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link href="/tools" className="text-accent hover:text-accent mb-4 inline-block">
            ← Back to Tools
          </Link>
          <h1 className="text-4xl font-bold mb-4 text-light-text dark:text-foreground">
            Technical Debt Paydown Simulator
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
            Model tech debt reduction over time, calculate velocity impact, and project ROI.
            Plan sustainable debt paydown strategies with data-driven insights.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Configuration */}
          <div className="space-y-6">
            {/* Current State */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Current State
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Current Tech Debt (days of effort)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={currentDebtDays}
                    onChange={(e) => setCurrentDebtDays(Math.max(1, parseInt(e.target.value) || 120))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    Total estimated effort to resolve all tech debt
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Team Size
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={teamSize}
                    onChange={(e) => setTeamSize(Math.max(1, parseInt(e.target.value) || 8))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Sprint Length (weeks)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={sprintLengthWeeks}
                    onChange={(e) => setSprintLengthWeeks(Math.max(1, parseInt(e.target.value) || 2))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Current Velocity (story points)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={currentVelocity}
                    onChange={(e) => setCurrentVelocity(Math.max(1, parseInt(e.target.value) || 40))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>
              </div>
            </div>

            {/* Time Allocation */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Time Allocation
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Debt Paydown Time: {debtAllocationPercent}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="5"
                    value={debtAllocationPercent}
                    onChange={(e) => setDebtAllocationPercent(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    <span>0% (No debt work)</span>
                    <span>50% (Half capacity)</span>
                  </div>
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-2">
                    = {calculations.debtDaysPerMonth.toFixed(1)} engineering days per month
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Velocity Impact from Debt: {velocityImpactPercent}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="5"
                    value={velocityImpactPercent}
                    onChange={(e) => setVelocityImpactPercent(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    <span>0% (No impact)</span>
                    <span>50% (High impact)</span>
                  </div>
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-2">
                    Paying down debt will increase velocity by up to {calculations.maxVelocityGain.toFixed(1)} points
                  </p>
                </div>
              </div>
            </div>

            {/* Debt Items */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-light-text dark:text-foreground">
                  Debt Items ({debtItems.filter(d => d.name.trim()).length})
                </h2>
                <button
                  onClick={addDebtItem}
                  className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors text-sm"
                >
                  + Add Item
                </button>
              </div>

              <div className="space-y-3">
                {debtItems.map((item) => (
                  <div key={item.id} className="border border-gray-300 dark:border-border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <input
                        type="text"
                        placeholder="Debt item name"
                        value={item.name}
                        onChange={(e) => updateDebtItem(item.id, 'name', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                      />
                      <button
                        onClick={() => removeDebtItem(item.id)}
                        className="ml-2 text-red-500 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          Effort (days)
                        </label>
                        <input
                          type="number" inputMode="numeric"
                          min="1"
                          value={item.effortDays}
                          onChange={(e) => updateDebtItem(item.id, 'effortDays', parseInt(e.target.value) || 1)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          Impact (1-10)
                        </label>
                        <input
                          type="number" inputMode="numeric"
                          min="1"
                          max="10"
                          value={item.impactScore}
                          onChange={(e) => updateDebtItem(item.id, 'impactScore', Math.min(10, Math.max(1, parseInt(e.target.value) || 5)))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          Priority
                        </label>
                        <select
                          value={item.priority}
                          onChange={(e) => updateDebtItem(item.id, 'priority', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                        >
                          <option value="critical">Critical</option>
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Simulation Length */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Simulation
              </h2>

              <div>
                <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                  Simulation Length (months)
                </label>
                <input
                  type="number" inputMode="numeric"
                  value={simulationMonths}
                  onChange={(e) => setSimulationMonths(Math.max(1, Math.min(36, parseInt(e.target.value) || 12)))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                />
                <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                  How far to project (1-36 months)
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Results */}
          <div className="space-y-6">
            {/* ROI Summary */}
            <div className={`border rounded-lg p-6 ${getROIBgColor()}`}>
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                ROI Analysis
              </h2>

              <div className="text-center mb-6">
                <div className={`text-4xl font-bold mb-2 ${getROIColor()}`}>
                  {calculations.isPositiveROI ? 'Positive ROI' : 'Negative ROI'}
                </div>
                <div className="text-light-text-secondary dark:text-foreground-secondary">
                  Net Gain: {calculations.netROI.toFixed(0)} story points over {simulationMonths} months
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Months to Paydown
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {calculations.monthsToPaydown}
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Months to Break-Even
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {calculations.monthsToBreakEven.toFixed(1)}
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Opportunity Cost
                  </div>
                  <div className="text-2xl font-bold text-red-500">
                    -{calculations.totalOpportunityCost.toFixed(0)}
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Velocity Gain
                  </div>
                  <div className="text-2xl font-bold text-green-500">
                    +{calculations.totalVelocityGain.toFixed(0)}
                  </div>
                </div>
              </div>
            </div>

            {/* Prioritized Debt Items */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Prioritized Debt Items
              </h2>

              {calculations.sortedDebtItems.length > 0 ? (
                <div className="space-y-3">
                  {calculations.sortedDebtItems.map((item, idx) => (
                    <div key={item.id} className="border border-gray-300 dark:border-border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold text-light-text-secondary dark:text-foreground-tertiary">
                              #{idx + 1}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded font-semibold ${getPriorityColor(item.priority)}`}>
                              {item.priority.toUpperCase()}
                            </span>
                          </div>
                          <div className="font-semibold text-light-text dark:text-foreground">{item.name}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-light-text-secondary dark:text-foreground-tertiary">Effort:</span>{' '}
                          <span className="font-semibold text-light-text dark:text-foreground">{item.effortDays} days</span>
                        </div>
                        <div>
                          <span className="text-light-text-secondary dark:text-foreground-tertiary">Impact:</span>{' '}
                          <span className="font-semibold text-light-text dark:text-foreground">{item.impactScore}/10</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-light-text-secondary dark:text-foreground-secondary">
                  Add debt items to see prioritized list
                </p>
              )}
            </div>

            {/* Projection Chart */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Velocity Projection
              </h2>

              <div className="space-y-2">
                {calculations.months.filter((_, i) => i % 3 === 0 || i === calculations.months.length - 1).map((m) => (
                  <div key={m.month} className="flex items-center gap-3">
                    <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary w-16">
                      Month {m.month}
                    </span>
                    <div className="flex-1 bg-gray-200 dark:bg-background rounded-full h-6 overflow-hidden">
                      <div
                        className="bg-accent h-full flex items-center justify-end px-2 transition-all"
                        style={{ width: `${(m.velocity / (currentVelocity + calculations.maxVelocityGain)) * 100}%` }}
                      >
                        <span className="text-xs font-semibold text-accent-foreground">
                          {m.velocity.toFixed(0)} pts
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-light-text-secondary dark:text-foreground-secondary mt-4">
                Velocity increases as debt is paid down, eventually reaching {(currentVelocity + calculations.maxVelocityGain).toFixed(0)} points
              </p>
            </div>

            {/* Recommendations */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Recommendations
              </h2>

              <div className="space-y-3 text-sm">
                {debtAllocationPercent < 10 && (
                  <div className="bg-accent-muted border border-border rounded-lg p-3">
                    <p className="text-accent font-semibold">
                      ⚠️ {debtAllocationPercent}% allocation may be too low. Consider 15-25% for sustainable paydown.
                    </p>
                  </div>
                )}

                {debtAllocationPercent > 40 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="text-red-500 font-semibold">
                      ⚠️ {debtAllocationPercent}% allocation may stall feature development. Consider 20-30% maximum.
                    </p>
                  </div>
                )}

                {!calculations.isPositiveROI && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="text-red-500 font-semibold">
                      ⚠️ Negative ROI over {simulationMonths} months. Consider longer timeframe or higher velocity impact.
                    </p>
                  </div>
                )}

                <div className="flex items-start gap-2">
                  <span className="text-accent">•</span>
                  <p className="text-light-text-secondary dark:text-foreground-secondary">
                    <span className="font-semibold text-light-text dark:text-foreground">Focus on critical items first:</span>{' '}
                    Highest impact debt will accelerate velocity gains
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-accent">•</span>
                  <p className="text-light-text-secondary dark:text-foreground-secondary">
                    <span className="font-semibold text-light-text dark:text-foreground">Consistent allocation:</span>{' '}
                    Maintain {debtAllocationPercent}% allocation every sprint for predictable paydown
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-accent">•</span>
                  <p className="text-light-text-secondary dark:text-foreground-secondary">
                    <span className="font-semibold text-light-text dark:text-foreground">Track velocity:</span>{' '}
                    Measure actual velocity improvements to validate impact assumptions
                  </p>
                </div>
              </div>
            </div>

            {/* Export */}
            <button
              onClick={exportToExcel}
              className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
            >
              📊 Export Simulation to Excel
            </button>

            {/* Save to Command Center */}
            {commandCenterEnabled && !saved && (
              <button
                onClick={handleSaveToCommandCenter}
                disabled={saving}
                className="w-full mt-3 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save to Command Center'}
              </button>
            )}
            {saved && (
              <div className="mt-3 px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved to Command Center
              </div>
            )}
          </div>
        </div>

        {/* Pro Features */}
        <div className="mt-8 bg-gradient-to-r from-accent-muted to-accent/5 border-l-4 border-accent p-6 rounded-lg">
          <h3 className="text-xl font-bold text-accent mb-3">⭐ Unlock Pro Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Pro ($29/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Multi-scenario comparison</li>
                <li>✓ Velocity impact analysis</li>
                <li>✓ Risk reduction visualization</li>
                <li>✓ Excel export with projections</li>
                <li>✓ Quarterly planning recommendations</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Team ($99/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Everything in Pro</li>
                <li>✓ Monte Carlo simulation for uncertainty</li>
                <li>✓ Team capacity modeling</li>
                <li>✓ Multi-year roadmap integration</li>
                <li>✓ Historical debt tracking</li>
              </ul>
            </div>
          </div>
          <Link
            href="/pricing"
            className="inline-block px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
          >
            Upgrade to Pro →
          </Link>
        </div>
      </div>
    </div>
  );
}
